// Deterministic final id renumbering (§ ids). Governance leaves survivor ids
// with gaps (merges/…) and globally-unique state ids; this one-time pass, run at
// the very end of ingest, renumbers everything to clean, contiguous, readable
// ids and rewrites all references. ids are opaque stable keys — this is only the
// clean initial numbering for the freshly-generated product (editing never
// renumbers). Pure: input never mutated; returns the doc + old→new id map.
//
//   episode  ep_NNN   by episodes[] order
//   scene    scn_NNN  global, by appearance order (scenes can move across eps)
//   actor    act_NNN  by first appearance in the script (per kind, contiguous)
//   location loc_NNN
//   prop     prp_NNN
//   state    default + st_NNN  per-asset scoped (default kept)

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isArr = (v: unknown): v is unknown[] => Array.isArray(v);
const pad = (n: number): string => String(n).padStart(3, "0");

export type IdMap = {
  episodes: Record<string, string>;
  scenes: Record<string, string>;
  actors: Record<string, string>;
  locations: Record<string, string>;
  props: Record<string, string>;
  states: Record<string, string>; // old global-unique state id → new per-asset id
};

const KINDS = [
  { key: "actors", idField: "actor_id", prefix: "act" },
  { key: "locations", idField: "location_id", prefix: "loc" },
  { key: "props", idField: "prop_id", prefix: "prp" },
] as const;

export function renumberIds(input: unknown): { script: Obj; idMap: IdMap } {
  if (!isObj(input)) throw new Error("renumberIds expects a script object");
  const doc = JSON.parse(JSON.stringify(input)) as Obj;
  const idMap: IdMap = { episodes: {}, scenes: {}, actors: {}, locations: {}, props: {} , states: {} };

  // 1. Asset ids by first appearance (document walk).
  const seen: Record<"actors" | "locations" | "props", string[]> = { actors: [], locations: [], props: [] };
  const seenSet: Record<"actors" | "locations" | "props", Set<string>> = { actors: new Set(), locations: new Set(), props: new Set() };
  const see = (bucket: "actors" | "locations" | "props", id: unknown) => { if (typeof id === "string" && !seenSet[bucket].has(id)) { seenSet[bucket].add(id); seen[bucket].push(id); } };
  for (const ep of isArr(doc.episodes) ? doc.episodes : []) {
    if (!isObj(ep) || !isArr(ep.scenes)) continue;
    for (const sc of ep.scenes) {
      if (!isObj(sc)) continue;
      if (isObj(sc.location)) see("locations", sc.location.location_id);
      for (const a of isArr(sc.actors) ? sc.actors : []) if (isObj(a)) see("actors", a.actor_id);
      for (const p of isArr(sc.props) ? sc.props : []) if (isObj(p)) see("props", p.prop_id);
      for (const ac of isArr(sc.actions) ? sc.actions : []) if (isObj(ac) && isObj(ac.speaker)) see("actors", ac.speaker.actor_id);
    }
  }
  // Append any asset present in the top-level lists but never referenced.
  for (const { key, idField } of KINDS) for (const a of isArr(doc[key]) ? (doc[key] as unknown[]) : []) if (isObj(a)) see(key as "actors" | "locations" | "props", a[idField]);

  for (const { key, prefix } of KINDS) seen[key as "actors" | "locations" | "props"].forEach((old, i) => { idMap[key as "actors" | "locations" | "props"][old] = `${prefix}_${pad(i + 1)}`; });

  // 2. Rebuild top-level asset lists in new-id order; renumber per-asset states.
  for (const { key, idField } of KINDS) {
    const list = (isArr(doc[key]) ? doc[key] : []).filter(isObj) as Obj[];
    const bucket = idMap[key as "actors" | "locations" | "props"];
    const byNew = new Map<string, Obj>();
    for (const a of list) {
      const oldId = a[idField] as string;
      const newId = bucket[oldId] ?? oldId;
      a[idField] = newId;
      // states: keep default, renumber the rest st_001.. in array order.
      let n = 0;
      for (const st of isArr(a.states) ? a.states : []) {
        if (!isObj(st) || typeof st.state_id !== "string") continue;
        if (st.state_id === "default") continue;
        const ns = `st_${pad(++n)}`;
        idMap.states[st.state_id] = ns;
        st.state_id = ns;
      }
      byNew.set(newId, a);
    }
    doc[key] = [...byNew.entries()].sort((x, y) => (x[0] < y[0] ? -1 : 1)).map((e) => e[1]);
  }

  // 3. Episode / scene ids (in order).
  let scn = 0;
  (isArr(doc.episodes) ? doc.episodes : []).forEach((ep, ei) => {
    if (!isObj(ep)) return;
    if (typeof ep.episode_id === "string") { const nid = `ep_${pad(ei + 1)}`; idMap.episodes[ep.episode_id] = nid; ep.episode_id = nid; }
    for (const sc of isArr(ep.scenes) ? ep.scenes : []) {
      if (!isObj(sc)) continue;
      if (typeof sc.scene_id === "string") { const nid = `scn_${pad(++scn)}`; idMap.scenes[sc.scene_id] = nid; sc.scene_id = nid; }
    }
  });

  // 4. Rewrite references (asset ids + state ids) throughout the scenes.
  const remapRef = (holder: unknown, idField: string, kind: "actors" | "locations" | "props") => {
    if (!isObj(holder) || typeof holder[idField] !== "string") return;
    holder[idField] = idMap[kind][holder[idField] as string] ?? holder[idField];
    if (typeof holder.state_id === "string" && idMap.states[holder.state_id]) holder.state_id = idMap.states[holder.state_id];
  };
  for (const ep of isArr(doc.episodes) ? doc.episodes : []) {
    if (!isObj(ep) || !isArr(ep.scenes)) continue;
    for (const sc of ep.scenes) {
      if (!isObj(sc)) continue;
      remapRef(sc.location, "location_id", "locations");
      for (const a of isArr(sc.actors) ? sc.actors : []) remapRef(a, "actor_id", "actors");
      for (const p of isArr(sc.props) ? sc.props : []) remapRef(p, "prop_id", "props");
      for (const ac of isArr(sc.actions) ? sc.actions : []) if (isObj(ac)) remapRef(ac.speaker, "actor_id", "actors");
    }
  }

  return { script: doc, idMap };
}
